/**
 * Token Manager - Gerenciamento seguro de tokens JWT
 *
 * Funcionalidades:
 * - Armazenamento seguro de tokens
 * - Validação de expiração
 * - Limpeza automática de tokens expirados
 * - Validação de formato JWT
 */
const TOKEN_KEY = 'app-token'
const TOKEN_METADATA_KEY = 'app-token-metadata'
const TOKEN_MAX_AGE = 24 * 60 * 60 * 1000 // 24 horas em milissegundos

export default {
  /**
   * Salva o token com metadata de segurança
   * @param {string} token - JWT token
   */
  setToken(token) {
    if (!this.isValidJWTFormat(token)) {
      throw new Error('Token inválido: formato JWT incorreto')
    }

    const metadata = {
      timestamp: Date.now(),
      expiresAt: Date.now() + TOKEN_MAX_AGE,
      userAgent: navigator.userAgent
    }

    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(TOKEN_METADATA_KEY, JSON.stringify(metadata))
  },

  /**
   * Recupera o token se válido e não expirado
   * @returns {string|null} Token ou null se inválido/expirado
   */
  getToken(accessToken = null) {
    const token = accessToken || localStorage.getItem(TOKEN_KEY)

    if (!token) {
      return null
    }

    // Verifica se o token expirou
    if (this.isTokenExpired()) {
      this.clearToken()
      return null
    }

    // Validação extra de formato
    if (!this.isValidJWTFormat(token)) {
      this.clearToken()
      return null
    }

    return token
  },

  /**
   * Verifica se o token expirou baseado no timestamp
   * @returns {boolean}
   */
  isTokenExpired() {
    const metadataStr = localStorage.getItem(TOKEN_METADATA_KEY)

    if (!metadataStr) {
      return true
    }

    try {
      const metadata = JSON.parse(metadataStr)
      const now = Date.now()

      // Verifica expiração
      if (metadata.expiresAt && now > metadata.expiresAt) {
        return true
      }

      // Verifica se é muito antigo (fallback)
      if (metadata.timestamp && now - metadata.timestamp > TOKEN_MAX_AGE) {
        return true
      }

      return false
    } catch (e) {
      console.error('Erro ao verificar expiração do token:', e)
      return true
    }
  },

  /**
   * Remove o token e metadata
   */
  clearToken() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(TOKEN_METADATA_KEY)
  },

  /**
   * Valida o formato básico de um JWT (header.payload.signature)
   * @param {string} token
   * @returns {boolean}
   */
  isValidJWTFormat(token) {
    if (!token || typeof token !== 'string') {
      return false
    }

    const parts = token.split('.')

    if (parts.length !== 3) {
      return false
    }

    // Verifica se cada parte parece ser base64url válido
    try {
      parts.forEach((part) => {
        if (part.length === 0) {
          throw new Error('Parte do token vazia')
        }
        // Tenta decodificar base64url
        let base64 = part.replace(/-/g, '+').replace(/_/g, '/')

        while (base64.length % 4) {
          base64 += '='
        }

        atob(base64)
      })
      return true
    } catch (e) {
      return false
    }
  },

  /**
   * Decodifica o payload do JWT (sem validação de assinatura)
   * AVISO: Isso é apenas para leitura, não para validação de segurança
   * @param {string} token
   * @returns {object|null}
   */
  decodeToken(token) {
    if (!this.isValidJWTFormat(token)) {
      return null
    }

    try {
      const parts = token.split('.')
      const payload = parts[1]
      let base64 = payload.replace(/-/g, '+').replace(/_/g, '/')

      while (base64.length % 4) {
        base64 += '='
      }
      const decoded = atob(base64)
      return JSON.parse(decoded)
    } catch (e) {
      console.error('Erro ao decodificar token:', e)
      return null
    }
  },

  /**
   * Verifica se há um token válido armazenado
   * @returns {boolean}
   */
  hasValidToken() {
    return this.getToken() !== null
  },

  /**
   * Obtém informações sobre o token atual
   * @returns {object|null}
   */
  getTokenInfo() {
    const token = this.getToken()

    if (!token) {
      return null
    }

    const metadataStr = localStorage.getItem(TOKEN_METADATA_KEY)
    const metadata = metadataStr ? JSON.parse(metadataStr) : {}
    const payload = this.decodeToken(token)

    return {
      hasToken: true,
      isExpired: this.isTokenExpired(),
      metadata,
      payload,
      expiresIn: metadata.expiresAt ? metadata.expiresAt - Date.now() : null
    }
  }
}
